import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Icon } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import FormValidator from 'utils/FormValidator';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorsUtils from 'utils/Errors';
import localeUtils from 'utils/LanguageLocale';
import UI from 'utils/UI';
import { colors, space } from 'style/config';

const SEARCH_ICON_SIZE = 16;

function FirstAddressField({
    address1 = '',
    isAdsRestricted = false,
    isAddressVerified = false,
    hasAVS = false,
    isShortenedLoqateForm = false,
    highlightedAddrIndex = -1,
    hasAddress1Focus = false,
    hasLoqateError = false,
    isFCReleaseThreeEnabled = false,
    isSubscription = false,
    modalTitle = '',
    localization = {},
    onKeyUp,
    onBlur,
    onFocus,
    onKeyDown,
    onChange,
    addressInputRef,
    fireFormErrorAnalytics,
    renderAVS
}) {
    const { streetAddress, startTyping } = localization;
    const isUS = localeUtils.isUS();

    const isRestrictedLength = isAdsRestricted || !isAddressVerified;

    const maxLengthVal = isRestrictedLength ? FormValidator.FIELD_LENGTHS.addressAdsRestricted : FormValidator.FIELD_LENGTHS.address;

    const getAddressError = addressVal => {
        if (FormValidator.isEmpty(addressVal)) {
            return ErrorConstants.ERROR_CODES.ADDRESS1;
        }

        if (!FormValidator.isValidLength(addressVal, 1, maxLengthVal)) {
            return isRestrictedLength ? ErrorConstants.ERROR_CODES.ADDRESS1_ADS_LONG : ErrorConstants.ERROR_CODES.ADDRESS1_LONG;
        }

        return null;
    };

    const validateAddress = addressVal => {
        const errorCode = getAddressError(addressVal);

        if (errorCode && isSubscription && fireFormErrorAnalytics) {
            fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(errorCode));
        }

        return errorCode;
    };

    /**
     * Hide the search icon whenever the field is invalid.
     */

    const hasValidationError = !!getAddressError(address1);

    const shouldShowSearchIcon =
        isShortenedLoqateForm && !hasLoqateError && !hasValidationError && !hasAddress1Focus && FormValidator.isEmpty(address1);

    const avsProps = hasAVS
        ? {
            'aria-autocomplete': 'list',
            'aria-controls': 'avs_listbox',
            'aria-activedescendant': highlightedAddrIndex > -1 ? `avs_result${highlightedAddrIndex}` : null,
            'aria-describedby': 'avs_keep_typing_message',
            id: 'avs_input',
            onBlur,
            onFocus,
            onKeyDown,
            onChange
        }
        : {};

    return (
        <Box position='relative'>
            <TextInput
                {...avsProps}
                name='address1'
                onKeyUp={onKeyUp}
                label={isShortenedLoqateForm ? (address1?.length > 0 ? streetAddress : startTyping) : streetAddress}
                autoComplete={hasAVS ? (UI.isChrome() ? 'nope' : 'off') : 'address-line1'}
                autoCorrect='off'
                required={true}
                hideAsterisk={isUS || isFCReleaseThreeEnabled}
                maxLength={maxLengthVal}
                value={address1}
                ref={addressInputRef}
                validateError={validateAddress}
                data-at={Sephora.debug.dataAt('street_address_input')}
                dataAtError={Sephora.debug.dataAt('address_required_error')}
            />

            {hasAVS && renderAVS && renderAVS()}

            {shouldShowSearchIcon && (
                <Icon
                    data-at={Sephora.debug.dataAt('search_icon')}
                    name='search'
                    size={SEARCH_ICON_SIZE}
                    color={colors.gray}
                    css={styles.searchIcon}
                />
            )}
        </Box>
    );
}

const styles = {
    searchIcon: {
        position: 'absolute',
        top: 13,
        right: space[3],
        pointerEvents: 'none'
    }
};

export default wrapFunctionalComponent(FirstAddressField, 'FirstAddressField');
